import type { ReturnRefundDestination } from "@redotech/customer-portal-sdk/rpc/schema/get-return-refund-destination";
import {
  closedReturnStatuses,
  ReturnStatus,
} from "@redotech/redo-return-core-common/return-status";
import {
  CustomerRefundProvider,
  isRefundDestinationProvider,
  recipientFieldForProvider,
  RefundRecipientField,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { RefundMethodSummaryProps } from "./refund-method-summary";

export interface ResolveRefundMethodSummaryInput {
  destination: ReturnRefundDestination | undefined;
  returnStatus: ReturnStatus | undefined;
  /** Total heading to the customer, in the return's currency. */
  refundTotal: number;
  /** Rails the team still offers, already filtered for the return's currency. */
  availableProviders: readonly CustomerRefundProvider[];
  onEditClick: () => void;
}

/**
 * Whether to show a refund destination on a submitted return, and whether to
 * offer editing it. Split out from the confirmation page so the gates can be
 * exercised directly — getting one wrong either hides a customer's refund
 * destination or invites them to edit one that will never be paid.
 */
export function resolveRefundMethodSummary({
  destination,
  returnStatus,
  refundTotal,
  availableProviders,
  onEditClick,
}: ResolveRefundMethodSummaryInput): RefundMethodSummaryProps | undefined {
  // Only a refund actually lands in a wallet. A $0 return keeps the rail the
  // customer picked, in case a merchant adjusts it upward later, but there is
  // nothing to show them until then.
  if (!destination || refundTotal <= 0) {
    return undefined;
  }

  // A bank account is addressed by an onboarded Stripe account, not a contact.
  const field = isRefundDestinationProvider(destination.provider)
    ? recipientFieldForProvider(destination.provider)
    : undefined;
  // A closed return will never pay out, and nothing ever cancels its
  // disbursement, so it would otherwise stay editable forever.
  const returnIsClosed =
    !returnStatus || closedReturnStatuses.includes(returnStatus);

  return {
    provider: destination.provider,
    recipient:
      field === undefined
        ? ""
        : ((field === RefundRecipientField.PHONE
            ? destination.recipient.phone
            : destination.recipient.email) ?? ""),
    status: destination.status,
    // A single entitled wallet is still worth editing: the recipient may be a
    // typo. A bank account can only be switched away from, never re-entered
    // here, so editing needs a wallet to switch to.
    canEdit:
      destination.canEdit &&
      !returnIsClosed &&
      availableProviders.some(isRefundDestinationProvider),
    onEditClick,
  };
}
